import { LatLon } from './latlon';


/**
 * Value to use as the minimum difference between two numbers in order for them to be considered not equal.
 */
export const EPSILON = 0.00001;


/**
 * Represents a 2D rectangle bounded by Northeast and Southwest corners (each represented by a LatLon coordinate).
 */
export class BoundingBox {
  private _ne: LatLon;
  private _sw: LatLon;

  /**
   * Gets the Northeast corner of the bounding box
   */
  get ne(): LatLon {
    return this._ne;
  }

  /**
   * Sets the Northeast corner of the bounding box. Note that the setter will re-check the orientation, so the value
   * may end up being different.
   */
  set ne(value: LatLon) {
    this._ne = value;
    this._checkOrientation();
  }

  /**
   * Gets the Southwest corner of the bounding box
   */
  get sw(): LatLon {
    return this._sw;
  }

  /**
   * Sets the Southwest corner of the bounding box. Note that the setter will re-check the orientation, so the value
   * may end up being different.
   */
  set sw(value: LatLon) {
    this._sw = value;
    this._checkOrientation();
  }

  /**
   * Creates an instance of the BoundingBox class given two corners. The corners may have arbitrary orientation - the
   * constructed instance will ensure that the "ne" property returns the Northeast corner, and "sw" returns the
   * Southwest corner.
   * @param corner1 {LatLon}
   * @param corner2 {LatLon}
   */
  constructor(corner1: LatLon, corner2: LatLon) {
    this._ne = corner1;
    this._sw = corner2;
    this._checkOrientation();
  }

  /**
   * Returns true if the two bounding boxes are roughly equal.
   * @param other {BoundingBox} Bounding box to compare to
   * @param [epsilon] {number} (Optional) Value to use as the minimum difference between two numbers in order for them
   *  to be considered not equal.
   * @returns {boolean} True if the bounding boxes are roughly equal
   */
  equalTo(other: BoundingBox, epsilon = EPSILON): boolean {
    return Math.abs(this.ne.lat - other.ne.lat) < epsilon
      && Math.abs(this.ne.lon - other.ne.lon) < epsilon
      && Math.abs(this.sw.lat - other.sw.lat) < epsilon
      && Math.abs(this.sw.lon - other.sw.lon) < epsilon;
  }

  /**
   * Returns the bounding box area in square degrees.
   * @returns {number}
   */
  getAreaDegrees(): number {
    return (this.sw.lon - this.ne.lon) * (this.sw.lat - this.ne.lat);
  }

  /**
   * Checks the orientation of the two corners of the bounding box so as to ensure that the Northeast corner is
   * actually Northeast, and that the Southwest corner actually is the Southwest. The corner LatLons will be updated
   * appropriately if the orientation is not correct.
   * @private
   */
  private _checkOrientation() {
    const minLat = Math.min(this._ne.lat, this._sw.lat);
    const minLon = Math.min(this._ne.lon, this._sw.lon);
    const maxLat = Math.max(this._ne.lat, this._sw.lat);
    const maxLon = Math.max(this._ne.lon, this._sw.lon);
    if (this._ne.lat !== maxLat || this._ne.lon !== maxLon) {
      this._ne = new LatLon(maxLat, maxLon);
    }
    if (this._sw.lat !== minLat || this._sw.lon !== minLon) {
      this._sw = new LatLon(minLat, minLon);
    }
  }
}
